/**
 * The settings save model (DESIGN.md "Save model"), written once.
 *
 * Every settings control commits itself — toggles and selects on change, text
 * and number inputs on blur — an unchanged value never fires a save, and a
 * failure renders inline on the row. Before this hook the same
 * draft/resync/saving/error machine was hand-rolled eight times (three copies
 * inside SettingRow itself), and the copies had drifted: the estimate editor
 * dropped the resync so a stale value could stick in its inputs, and the Xero
 * account codes routed row failures to a page-top banner.
 *
 * Contract:
 *   - `draft` mirrors `stored` until the user edits; an external change to
 *     `stored` (a refetch, another row's save response) resyncs the draft —
 *     unless the control is focused (`onFocus` arms the guard; committing or
 *     resetting disarms it), so a concurrent refetch can't stomp typing.
 *     `stored` is compared by value (`equals`), never identity, so callers
 *     may build it inline per render.
 *   - `commitDraft()` is the blur commit: it saves the draft only when it
 *     differs from `stored` (per `equals`, default `Object.is`).
 *   - A successful save stands in for `stored` until `stored` itself
 *     changes. A caller that does not write the response back would
 *     otherwise compare every later blur against the value the page
 *     loaded with: Settings -> Questions' form name did, so renaming it
 *     back was never saved and every other blur sent it again.
 *   - `commit(next)` is the change commit for toggles and selects: it saves
 *     `next` immediately, no draft involved.
 *   - `run(fn)` runs any other row action (an upload, a removal) under the
 *     same saving/error machine.
 *   - A failure keeps the draft (so the operator can correct it) and surfaces
 *     the ApiError-aware message in `error` for the row's inline slot.
 */
import { useEffect, useRef, useState } from "react";
import { track } from "./analytics";
import { useAction } from "./use-action";

export interface SettingCommitOptions<T> {
  /** Value comparison for the unchanged-guard and the resync; default Object.is. */
  equals?: (a: T, b: T) => boolean;
}

export interface SettingCommit<T> {
  /** The edit buffer; mirrors `stored` until the user edits. */
  draft: T;
  setDraft: (next: T) => void;
  /** Arm the focus guard so a concurrent refetch can't stomp typing. */
  onFocus: () => void;
  /** True while a save (or `run` action) is in flight; disable the control. */
  saving: boolean;
  /** Failure message for the row's inline error slot, or null. */
  error: string | null;
  setError: (next: string | null) => void;
  /** Blur commit: saves the draft unless it equals `stored`. */
  commitDraft: () => Promise<boolean>;
  /** Change commit (toggles/selects): saves `next` immediately. */
  commit: (next: T) => Promise<boolean>;
  /** Revert the draft to `stored` without saving (required-empty guard). */
  resetDraft: () => void;
  /** Run any other row action under the same saving/error machine. */
  run: (fn: () => Promise<unknown>) => Promise<boolean>;
}

export function useSettingCommit<T>(
  stored: T,
  save?: (next: T) => Promise<unknown>,
  options: SettingCommitOptions<T> = {},
): SettingCommit<T> {
  const { equals = Object.is } = options;
  const [draft, setDraft] = useState<T>(stored);
  const { working: saving, error, setError, run } = useAction();
  const [saved, setSaved] = useState<{ value: T } | null>(null);
  const focusedRef = useRef(false);
  const base = saved ? saved.value : stored;

  // Resync on external change. The previous `stored` is tracked by value so
  // the effect can run every render: an inline-built `stored` object with
  // unchanged contents never counts as a change, and a genuinely new value
  // overwrites the draft only while the control is not focused.
  const prevStoredRef = useRef(stored);
  useEffect(() => {
    if (equals(prevStoredRef.current, stored)) return;
    prevStoredRef.current = stored;
    setSaved(null);
    if (focusedRef.current) return;
    setDraft(stored);
  });

  const commit = (next: T): Promise<boolean> => {
    if (!save) {
      throw new Error("useSettingCommit: commit requires a save function");
    }
    return run(() => save(next)).then((ok) => {
      if (ok) {
        setSaved({ value: next });
        track("change_saved");
      }
      return ok;
    });
  };

  const commitDraft = (): Promise<boolean> => {
    focusedRef.current = false;
    if (equals(draft, base)) return Promise.resolve(true);
    return commit(draft);
  };

  const resetDraft = () => {
    focusedRef.current = false;
    setDraft(base);
  };

  return {
    draft,
    setDraft,
    onFocus: () => {
      focusedRef.current = true;
    },
    saving,
    error,
    setError,
    commitDraft,
    commit,
    resetDraft,
    run,
  };
}
